import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import { catalogSettingsQuery, saveCatalogVisibility } from '../../api/settingsCatalog'
import { SchedulesPanel } from '../company_reports/CompanyReportsPage'
import { CatalogWidgetPanel } from './CatalogWidgetPanel'
import { Forbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> Service Catalog (zona Policies), element cu element fata de
 * `views/version-3/settings/catalog/index.php`, deschisa cu `$showVisibility`:
 * INTAI "What requesters see" - cele doua comutatoare ale magazinului - si
 * dedesubt "Scheduled delivery", aceeasi lista de programari ca in Settings >
 * Reports > Scheduled Reports (`SchedulesPanel`, o singura macheta acolo, un
 * singur component aici; doua liste s-ar fi despartit la prima corectura).
 *
 * Adresa e cea din `SettingsMenu` (`settings/catalog`), dupa care se aprinde
 * randul din rail-ul Policies. Textele sunt ale machetei, cuvant cu cuvant.
 */
function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

export function CatalogSettingsPage() {
  const client = useQueryClient()
  const page = useQuery(catalogSettingsQuery())
  const [showCost, setShowCost] = useState(true)
  const [showSla, setShowSla] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!page.data) return
    setShowCost(page.data.visibility.show_cost === 1)
    setShowSla(page.data.visibility.show_sla === 1)
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const editable = page.data.canWrite && page.data.visibilityStorage

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void saveCatalogVisibility({ show_cost: showCost, show_sla: showSla })
      .then((result) => {
        setBusy(false)
        toast(result.message, 'Service catalog', result.status === 'OK' ? 'success' : 'error')
        void client.invalidateQueries({ queryKey: ['settings', 'catalog'] })
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error), 'Service catalog', 'error')
      })
  }

  return (
    <div id="content" className="scheduleScreen">
      <header className="panel-heading">
        <h3>
          <strong>What requesters see</strong> in the shop
        </h3>
        <label className="color">the two figures the catalogue takes from the contract</label>
      </header>

      {!page.data.visibilityStorage && (
        <div className="alert alert-warning">
          <i className="fas fa-database"></i> The table these settings live in does not exist yet, so nothing can be saved
          here. Both figures are shown, as they are today. The statement is in <code>docs/catalog-ce-vede-clientul.md</code>.
        </div>
      )}

      <form id="catalogVisibility" className="form-horizontal" style={{ marginBottom: 24 }} onSubmit={submit}>
        <div className="form-group">
          <div className="col-sm-12">
            <label className="adm-check">
              <input
                type="checkbox"
                name="show_cost"
                value="1"
                checked={showCost}
                disabled={!editable}
                onChange={(event) => setShowCost(event.target.checked)}
              />
              <span>
                <strong>Service cost</strong> &mdash; what the service costs, usually per month
              </span>
            </label>
            <p className="text-muted" style={{ margin: '2px 0 10px 24px' }}>
              Comes from the cost rules and the commercial template. It is the cost of running the service, not the
              price of the request in front of the person.
            </p>

            <label className="adm-check">
              <input
                type="checkbox"
                name="show_sla"
                value="1"
                checked={showSla}
                disabled={!editable}
                onChange={(event) => setShowSla(event.target.checked)}
              />
              <span>
                <strong>Response SLA</strong> &mdash; how fast an incident on the service is answered
              </span>
            </label>
            <p className="text-muted" style={{ margin: '2px 0 10px 24px' }}>
              The response target from the contract. It is not a delivery time: a service answered in fifteen minutes
              is not a service delivered in fifteen minutes.
            </p>

            {editable && (
              <button type="submit" className="btn btn-theme btn-sm" disabled={busy}>
                Save
              </button>
            )}
          </div>
        </div>
      </form>

      <hr />

      <CatalogWidgetPanel />

      <hr />

      <SchedulesPanel />
    </div>
  )
}
